import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/useAuth";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import TermsDialog from "@/components/footer/terms_dialog";
import PrivacyPolicyDialog from "@/components/footer/privacy_policy_dialog";
import {
  buildRedirectAwarePath,
  getSafeRedirectOrDefault,
} from "@/utils/redirectUtils";
import { useLanguage } from "@/context/LanguageContext";
import { authLabels } from "@/i18n";

const AcceptTerms = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const labels = useMemo(() => authLabels(language), [language]);
  const t = labels.acceptTerms || {};
  const redirectParam = new URLSearchParams(location.search).get("redirect");
  const postTermsRedirect = getSafeRedirectOrDefault(redirectParam);
  const { user, acceptTermsAsync, isAcceptingTerms } = useAuth();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTermsDialog, setShowTermsDialog] = useState(false);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [isRedirectingAfterTerms, setIsRedirectingAfterTerms] = useState(false);

  // Redirect to login if not logged in
  useEffect(() => {
    if (!user) {
      navigate(buildRedirectAwarePath("/auth", postTermsRedirect), {
        replace: true,
      });
    }
  }, [user, navigate, postTermsRedirect]);

  // Redirect to intended destination if already accepted
  useEffect(() => {
    if (user?.emailVerified === true && user?.accountTermsAccepted === true) {
      navigate(postTermsRedirect, { replace: true });
    }
  }, [
    user?.emailVerified,
    user?.accountTermsAccepted,
    navigate,
    postTermsRedirect,
  ]);

  const handleContinueAfterTerms = async () => {
    if (!termsAccepted || !privacyAccepted) return;

    // User must be logged in to accept terms
    if (!user) {
      toast.info(t.pleaseLogin);
      navigate(buildRedirectAwarePath("/auth", postTermsRedirect));
      return;
    }

    try {
      // Show loading state immediately
      setIsRedirectingAfterTerms(true);

      // User is logged in - use authenticated endpoint
      await acceptTermsAsync();

      // State is now updated via acceptTermsMutation.onSuccess -> updateUser.
      // The useEffect above handles navigation once accountTermsAccepted is true.
      toast.success(t.successRedirect);
    } catch (e: unknown) {
      // Reset loading state on error
      setIsRedirectingAfterTerms(false);
      const error = e as { message?: string; status?: number };
      const message = error?.message ?? t.errorAccepting;

      // If unauthorized, redirect to login
      if (error?.status === 401) {
        toast.info(t.pleaseLogin);
        navigate(buildRedirectAwarePath("/auth", postTermsRedirect));
      } else {
        toast.error(message);
      }
    }
  };

  // Show loading overlay when redirecting after accepting terms
  if (isRedirectingAfterTerms) {
    return (
      <div className="min-h-screen flex items-center justify-center fixed inset-0 z-50 backdrop-blur-sm bg-background/80">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg font-medium text-foreground">{t.redirecting}</p>
        </div>
      </div>
    );
  }

  // Don't render if user is not logged in or email not verified
  if (!user || user.emailVerified === false) {
    return null;
  }

  // Don't render if already accepted (useEffect will handle redirect)
  if (user.accountTermsAccepted === true) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6 space-y-6 text-center glass-card">
        {/* Success Message */}
        <div className="flex flex-col items-center justify-center py-4 space-y-4">
          <div className="relative">
            <CheckCircle2 className="h-20 w-20 text-green-500 animate-in zoom-in duration-500" />
            <div className="absolute inset-0 bg-green-500 rounded-full animate-ping opacity-30" />
            <div className="absolute inset-0 bg-green-500/20 rounded-full animate-pulse" />
          </div>
          <p className="text-xl font-bold text-green-600 dark:text-green-400 animate-in fade-in duration-500">
            {t.welcome}
          </p>
          <p className="text-sm text-muted-foreground animate-in fade-in duration-700">
            {t.verifyEmailMessage}
          </p>
        </div>

        {/* Terms Checkboxes */}
        <div className="space-y-4 text-left">
          <div className="flex items-start gap-3">
            <Checkbox
              id="terms"
              checked={termsAccepted}
              onCheckedChange={(checked) => setTermsAccepted(checked === true)}
              className="mt-1"
            />
            <label
              htmlFor="terms"
              className="text-sm cursor-pointer flex-1 leading-relaxed"
            >
              {t.termsCheckboxIntro}{" "}
              <button
                type="button"
                onClick={() => setShowTermsDialog(true)}
                className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
              >
                {t.termsLink}
              </button>{" "}
              <span className="text-red-500">*</span>
            </label>
          </div>

          <div className="flex items-start gap-3">
            <Checkbox
              id="privacy"
              checked={privacyAccepted}
              onCheckedChange={(checked) =>
                setPrivacyAccepted(checked === true)
              }
              className="mt-1"
            />
            <label
              htmlFor="privacy"
              className="text-sm cursor-pointer flex-1 leading-relaxed"
            >
              {t.termsCheckboxIntro}{" "}
              <button
                type="button"
                onClick={() => setShowPrivacyDialog(true)}
                className="text-blue-600 dark:text-blue-400 font-bold hover:underline"
              >
                {t.privacyLink}
              </button>{" "}
              <span className="text-red-500">*</span>
            </label>
          </div>
        </div>

        {/* Continue Button */}
        <Button
          className="w-full"
          onClick={handleContinueAfterTerms}
          disabled={!termsAccepted || !privacyAccepted || isAcceptingTerms}
        >
          {isAcceptingTerms ? t.processing : t.continue}
        </Button>
      </Card>

      {/* Terms Dialog */}
      <TermsDialog
        open={showTermsDialog}
        onOpenChange={setShowTermsDialog}
        initialTab="general"
        generalTermsAccepting
      />

      {/* Privacy Dialog */}
      <PrivacyPolicyDialog
        open={showPrivacyDialog}
        onOpenChange={setShowPrivacyDialog}
      />
    </div>
  );
};

export default AcceptTerms;
